<template>
  <div>
    <div class="card-header" v-if="!locked">
        <print-settings-form
            v-if="componentOptions"
            :key="`pageComponent_options_${componentName}`"
            :formData="componentOptions"
            @update="$_updatedOptionsForm($event)"
        />
    </div>
    <b-card-body :class="{'custom-value-in-inputfield': mode==='custom'}">
        <print-settings-form
            v-if="componentWithoutOptions"
            :key="`pageComponent_${componentName}`"
            :formData="componentWithoutOptions"
            @update="$_updatedForm($event)"
        />
     </b-card-body>
  </div>
</template>

<script>
    import PrintSettingsForm from '../PrintSettingsForm.vue';

    const randomId = () => performance.now().toString().replace('.', '');

    export default {
        components: { 'print-settings-form': PrintSettingsForm },
        props: {
            customComponents: { type: Object, required: false },
            componentConfiguration: { type: Object, required: true },
            componentName: { type: String, required: true },
            printConfiguration: { type: Object, required: true },
            mode: { type: String, required: false, default: 'shared' }
        },

        data() {
            return {
                userComponentConfiguration: null,
                userCustomComponentSettings: null
            };
        },
        watch: {
            componentConfiguration: {
                handler: 'init',
                immediate: true
            },
            customComponents: {
                handler: 'init',
                immediate: true
            }
        },
        methods: {
            init () {
                this.userComponentConfiguration = JSON.parse(JSON.stringify(this.componentConfiguration));
                if (this.customComponents) {
                    this.userCustomComponentSettings = JSON.parse(JSON.stringify(this.customComponents));
                    if (!this.userCustomComponentSettings[this.componentName]){
                        this.userCustomComponentSettings[this.componentName] = {};
                    }
                }
            },
            $_updateFormGroup(customComponentSettings, item, data){
                // create an empty object if the setting does not exist
                if (!customComponentSettings[item]){
                    customComponentSettings[item] = {};
                }
                // iterate over the fields in the group
                for (const subitem in data[item]){
                    // copy the value to the custom settings for the selected paper format
                    if (customComponentSettings[item][subitem] !== data[item][subitem]) {
                        customComponentSettings[item][subitem] = data[item][subitem];
                    }
                    // delete the nested setting if it is simular to the default nested setting
                    if (this.userComponentConfiguration[item][subitem] === customComponentSettings[item][subitem]){
                        delete customComponentSettings[item][subitem];
                    }
                }
                // delete groups without a value
                if (Object.keys(customComponentSettings[item]).length < 1) {
                    delete customComponentSettings[item];
                }
            },
            $_updatedOptionsForm(data){
                data.options.required = true;
                this.$_updatedForm(data);
            },
            $_updatedForm(data){
                if (!this.customComponents) {
                    // we are changing a default setting
                    this.userComponentConfiguration = { ...this.userComponentConfiguration, ...data };
                    this.$emit('changeDefaultSettings', this.userComponentConfiguration);
                    return;
                }
                // we are changing settings of a component for a specific paper format
                const customComponentSettings = this.userCustomComponentSettings[this.componentName];
                // for each setting in the form
                for (const item in data){
                    // check if the setting is a group of settings like position or css
                    if (typeof this.userComponentConfiguration[item] === 'object') {
                        this.$_updateFormGroup(customComponentSettings, item, data);
                    } else if (customComponentSettings[item] !== data[item]) {
                        // if the setting is not a group and the value has changed, update the value
                        customComponentSettings[item] = data[item];
                    }
                    // delete the setting if it is similar to the default setting
                    if (customComponentSettings[item] !== undefined && (this.userComponentConfiguration[item] === customComponentSettings[item])){
                        delete customComponentSettings[item];
                    }
                }
                this.userCustomComponentSettings = { ...this.userCustomComponentSettings };
                this.$emit('saveSettings', this.userCustomComponentSettings);
            },
            $_getElementConfig(elementData, marker) {
                // create config for a component using the properties (keys) of the data object
                return Object.keys(elementData)
                    .map(key => {
                        // if there are defenitions for elements, try to find config there
                        if (this.printConfiguration.components && this.printConfiguration.components.elements) {
                            const elementConfig = this.printConfiguration.components.elements.find(({ name }) => name === key);
                            if (elementConfig) {
                                // if the found configuration is of type form, get the configuation for its fields (elements)
                                if (elementConfig.type === 'form' ) {
                                    return { ...elementConfig, elements: this.$_getElementConfig(elementData[key], randomId()) };
                                }
                                // if a configuration for a field is found, return it with a unique id
                                return { ...elementConfig, id: `${marker}_${key}` };
                            }
                        }
                        // if no configuration is found, use a textfield
                        return {
                            id: `${marker}_${key}`,
                            name: key,
                            text: `printComponent.dashboard.${key}`,
                            type: 'text',
                            required: true,
                            value: null,
                            valid: null,
                            placeholder: key,
                            invalidMessage: 'printComponent.dashboard.invalidInput',
                            headerText: key,
                        };
                    });
            },
            $_getElementsValue(elementProps, values, customValues) {
                // for a set of fields (elements) set the current value
                return elementProps.map(field => {
                    // if a field has elements we are dealing with a form in a form
                    if (field.elements) {
                        // fill the values for this sub-form
                        const customSubValues = customValues ? customValues[field.name] : undefined;
                        return { ...field, elements: this.$_getElementsValue(field.elements, values[field.name], customSubValues), value: customSubValues || values[field.name] };
                    }
                    // for input-field we set the current or default value
                    const defaultvalue = values?.[field.name] !== undefined ? values[field.name] : field.value;
                    const customvalue = customValues?.[field.name];
                    const value = customvalue || defaultvalue;
                    const _class = customValues?.[field.name] !== undefined ? 'has-custom-value' : '';
                    const fieldSettings = {
                        ...field,
                        text: this.$t(field.text),
                        invalidMessage: this.$t(field.invalidMessage),
                        value,
                        class: _class
                    };
                    if (field.type === 'decimal-number') {
                        fieldSettings.defaultValue = defaultvalue;
                    }
                    return fieldSettings;
                });
            }
        },
        computed: {
            locked: function () {
                return this.printConfiguration?.components?.configuration?.[this.componentName].required;
            },
            componentForm: function () {
                const formData = {};
                // if there is no config available for a component, create it
                if (!formData[this.componentName]){
                    formData[this.componentName] = this.$_getElementConfig(this.componentConfiguration, randomId());
                }
                // fill each field in the form with the current value
                const currentCustomComponentSettings = this.userCustomComponentSettings?.[this.componentName];
                return this.$_getElementsValue(formData[this.componentName], this.userComponentConfiguration, currentCustomComponentSettings);
            },
            componentWithoutOptions: function () {
                if (!this.componentForm) {
                    return undefined;
                }
                return this.componentForm.filter(({ name }) => name !== 'options');
            },
            componentOptions: function () {
                if (!this.componentForm) {
                    return undefined;
                }
                const optionsFormOriginal = this.componentForm.find(({ name }) => name === 'options');
                if (!optionsFormOriginal) {
                    return undefined;
                }
                const optionsForm = JSON.parse(JSON.stringify(optionsFormOriginal));
                // remove the required input from the options, this value is managed in pageconfiguration.vue
                optionsForm.elements = optionsForm.elements.filter(({ name }) => name !== 'required');
                // since the form element also contain values set in default, unavailable in template-context,
                optionsForm.elements.forEach(element => {
                    if ( optionsForm.value[element.name] === undefined){
                        // copy the missing values into the state from the form fields
                        optionsForm.value[element.name] = element.value;
                    } else {
                        // otherwise syncronize the form values with the current state
                        element.value = optionsForm.value[element.name];
                    }
                });
                const inittialFormElementSettings = optionsForm.elements.find(({ name }) => name === 'initial');
                if (optionsForm.value.optional !== true) {
                    // initial will be set to true, the component will allways be rendered for print
                    optionsForm.value.initial = true;
                    // the formfield for initial can be disabled, but shows the selected state
                    inittialFormElementSettings.disabled = true;
                    inittialFormElementSettings.value = true;
                } else {
                    // the formfield for initial needs to be enabled
                    inittialFormElementSettings.disabled = false;
                }
                if (optionsForm.value.initial === undefined) {
                    // use the defaultvalue or true if no value is available
                    optionsForm.value.initial = this.printConfiguration?.components?.configuration?.[this.componentName].initial ?? true;
                }
                return [optionsForm];
            },

        },
    };
</script>

<style lang="scss">
    .custom-value-in-inputfield {
        color: rgb(184, 184, 184);
        input, select {
            color: rgb(184, 184, 184);
        }
    }
    .has-custom-value {
        color: black;
        input, select {
            color: black;
        }
    }
</style>
